import type { MessageKey } from '@itin/shared'
import { useEffect, useState } from 'react'
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useT } from '../lib/i18n.js'
import { useGuidanceLevel } from '../lib/guidance.js'
import { ApiError } from '../lib/api.js'

/** Shared primitives, on Ana's palette. */

/**
 * Four weights, and the difference between them is the point.
 *
 * `primary` is the navy fill and there should be at most one visible at a time —
 * on a screen where every button is filled, none of them is the answer. `quiet`
 * is the workhorse: an outline that is legible without competing. `ghost` is for
 * the actions that belong to one row and would otherwise turn a list into a wall
 * of boxes. `danger` stays outlined rather than filled red, because a delete
 * control should be findable without being alarming.
 *
 * Every button is 44px tall on a phone and shrinks from `sm:` up.
 *
 * 44 is Apple's minimum tap target and 48 is Google's; the previous 40/32 pair
 * was chosen by looking at a 1280px window, which is the only width this
 * project's browser suite ever ran at. On a phone a 32px control sits inside a
 * row of other 32px controls, and the one Ana hits is a coin toss — which
 * matters most for `danger`, sitting a thumb's width from `edit`.
 */
export function Button({
  variant = 'quiet',
  size = 'md',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'quiet' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
}) {
  const styles = {
    primary:
      'bg-navy text-cream hover:bg-navy-soft border border-navy shadow-[0_1px_2px_rgba(10,14,31,0.18)]',
    quiet: 'border border-slate-300 bg-white text-slate-800 hover:border-gold hover:bg-[#fdfaf3]',
    ghost: 'border border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    danger: 'border border-transparent text-red-700 hover:border-red-200 hover:bg-red-50',
  }[variant]

  const sizing =
    size === 'sm'
      ? 'h-11 px-3 text-xs gap-1.5 sm:h-8 sm:px-2.5'
      : 'h-11 px-4 text-sm gap-2 sm:h-10'

  return (
    <button
      {...props}
      className={`inline-flex shrink-0 items-center justify-center rounded-md font-medium transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:cursor-not-allowed disabled:opacity-45 ${sizing} ${styles} ${className}`}
    />
  )
}

/**
 * A labelled control, and the only place a field explains itself.
 *
 * `guidance` is a message key rather than a sentence, so retiring the
 * explanations later is a deletion from two catalogue files — not a hunt
 * through JSX for prose that got typed inline. See `lib/guidance.tsx`.
 */
export function Field({
  label,
  hint,
  guidance,
  error,
  children,
}: {
  label: string
  hint?: string
  guidance?: MessageKey
  error?: string | undefined
  children: ReactNode
}) {
  const t = useT()
  const level = useGuidanceLevel()

  // 'full' is the long explanation on top of the short hint; 'brief' keeps only
  // the hint; 'off' shows neither. One switch, three settings, nothing per-field.
  const showHint = hint && level !== 'off'
  const showGuidance = guidance && level === 'full'

  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
      {showHint && <span className="text-xs leading-snug text-slate-500">{hint}</span>}
      {showGuidance && (
        <span className="border-l-2 border-gold/40 pl-2.5 text-xs leading-relaxed text-slate-500">
          {t(guidance)}
        </span>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </label>
  )
}

/**
 * The shared shape of every control, and the reason it is 16px on a phone.
 *
 * iOS Safari zooms the page whenever a focused field's font-size is under 16px,
 * and it does not zoom back out. At `text-sm` — 14px — every tap into every
 * field on Ana's iPhone jerked the page sideways and left it magnified, and the
 * only way back was to pinch. That one number is most of what "the mobile UX is
 * bad" felt like. 16px on a phone, back to 14 from `sm:` up where the extra
 * size is just weight.
 *
 * Matching height: 44px on a phone (Apple's minimum), 40 above it.
 */
export const controlClass =
  'h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/25 sm:h-10 sm:text-sm'

/** A textarea gets the same treatment, minus the fixed height. */
export const textAreaClass =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/25 sm:text-sm'

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${controlClass} ${className}`} />
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${controlClass} ${className}`} />
}

export function TextArea({ className = '', ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${textAreaClass} ${className}`} />
}

/**
 * A panel that takes over the screen on a phone and stays inline above it.
 *
 * Three class strings rather than a component, because the thing that needs
 * this shape is a `<form>` — its submit button has to stay inside the same
 * element as its fields, so a wrapper component would have to render the form
 * tag itself. Applied to the form directly, the markup is identical at every
 * width: one DOM tree, no forked render path, and a browser test finds the same
 * fields on a phone as on a laptop.
 *
 * Why it takes over at all: with the field explanations on — which is Ana's
 * default and the point of building them — the add-an-item form is about 1,500px
 * tall. Inline on a 844px phone, that puts "Save" two screens below the field
 * she just filled, and pushes the rest of the day off-screen while she types.
 */
export const sheetShell =
  'flex flex-col gap-4 rounded-md border border-slate-200 bg-[#fdfcf9] p-4 ' +
  'max-sm:fixed max-sm:inset-0 max-sm:z-40 max-sm:gap-0 max-sm:rounded-none max-sm:border-0 max-sm:bg-white max-sm:p-0'

/** The scrolling middle. Only scrolls when the sheet is full-screen. */
export const sheetBody = 'flex flex-col gap-4 max-sm:min-h-0 max-sm:flex-1 max-sm:overflow-y-auto max-sm:p-4'

/**
 * The action bar. Pinned to the bottom of a phone, in the flow above it.
 * `pb-[env(safe-area-inset-bottom)]` keeps it clear of the iPhone home bar.
 */
export const sheetBar =
  'flex gap-2 ' +
  'max-sm:sticky max-sm:bottom-0 max-sm:z-10 max-sm:border-t max-sm:border-slate-200 max-sm:bg-white max-sm:p-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]'

/** The title row a full-screen sheet needs and an inline panel does not. */
export const sheetHead =
  'hidden max-sm:sticky max-sm:top-0 max-sm:z-10 max-sm:flex max-sm:items-center max-sm:justify-between max-sm:gap-3 max-sm:border-b max-sm:border-slate-200 max-sm:bg-white max-sm:px-4 max-sm:py-3'

/**
 * Stops the page behind a full-screen sheet from scrolling under it.
 *
 * Only below `sm`, because above it the sheet is an inline panel and locking
 * the page would strand somebody mid-form on a laptop.
 */
export function useSheetScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    const phone = window.matchMedia('(max-width: 639px)')
    if (!phone.matches) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [active])
}

export function Card({
  title,
  actions,
  children,
  testId,
  collapsible = false,
}: {
  title: string
  actions?: ReactNode
  children: ReactNode
  /** Lets a browser test address one card. Several cards now list the same
   *  participant names, so an unscoped getByText is ambiguous by design. */
  testId?: string
  /**
   * Folds this card away on a phone. Ignored above `sm`.
   *
   * A trip page is one itinerary and seven supporting cards — money, travellers,
   * documents, links. On a laptop that is a long page. On a 390px phone it was
   * 4,700px, and the itinerary — the reason Ana opened the trip — was the second
   * of eight things she had to scroll through to reach anything else.
   *
   * Width-dependent on purpose. Folding them on a laptop would hide things that
   * are perfectly readable there, and the collapse is done in CSS from one piece
   * of state, so the markup is identical either way: everything a browser test
   * or a find-in-page looks for is still in the document.
   */
  collapsible?: boolean
}) {
  const t = useT()
  const [open, setOpen] = useState(false)

  return (
    <section
      data-testid={testId}
      className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        {/*
          `font-sans`, overriding the display face the stylesheet gives every
          h2. MuseoModerno's uppercase G carries a spur that reads as a Q at
          this size — "MEUS LUGARES" rendered as "MEUS LUQARES" and "VIAGEM" as
          "VIAQEM". The display face stays on mixed-case headings, where it is
          doing the job it was picked for.
        */}
        {/* Cinzel, letterspaced, small. A titling face is unreadable at length,
            so it is only ever three or four words — see `.label-caps`. */}
        <h2 className="label-caps text-slate-500">{title}</h2>
        {collapsible && (
          <button
            type="button"
            data-testid={testId ? `${testId}-toggle` : undefined}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="-my-1.5 ml-auto flex min-h-11 items-center gap-2 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-50 hover:text-slate-800 sm:hidden"
          >
            {open ? t('common.collapse') : t('common.expand')}
            <span aria-hidden="true">{open ? '▾' : '▸'}</span>
          </button>
        )}
        {/*
          One instance, hidden by CSS — never a second copy for the phone.

          It was briefly rendered twice, once per breakpoint, which put two
          inputs carrying the same `aria-label` in the document at the same
          time. Screen readers announce both, `getByLabel` matches both, and a
          value typed into the visible one belongs to whichever node React
          reconciles first. A control that exists twice is a bug wherever it is
          hidden.
        */}
        {actions && (
          <div className={collapsible && !open ? 'max-sm:hidden' : undefined}>{actions}</div>
        )}
      </header>
      <div className={`p-4 ${collapsible && !open ? 'max-sm:hidden' : ''}`}>{children}</div>
    </section>
  )
}

/**
 * Renders an error by code, never by whatever text arrived from the network.
 * The server sends codes precisely so this stays translatable.
 */
export function ErrorNotice({ error }: { error: unknown }) {
  const t = useT()
  if (!error) return null
  const code = error instanceof ApiError ? error.code : 'internal'
  return (
    <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {t(`error.${code}`)}
    </p>
  )
}

/** Inline error text, for places where a full alert box would be too loud. */
export function ErrorText({ children }: { children: ReactNode }) {
  return <span className="text-xs text-red-700">{children}</span>
}

/**
 * A field's explanation, rendered at the width of a row rather than a column.
 *
 * Needed because guidance inside a narrow grid cell wraps to eight lines and
 * turns a two-field row into a wall — the time field is 7rem wide and its
 * sentence is not. Same setting, same catalogue key, different place to put it.
 */
export function GuidanceNote({ keys }: { keys: MessageKey[] }) {
  const t = useT()
  const level = useGuidanceLevel()
  if (level !== 'full') return null
  return (
    <div className="flex flex-col gap-1 border-l-2 border-gold/40 pl-2.5">
      {keys.map((key) => (
        <span key={key} className="text-xs leading-relaxed text-slate-500">
          {t(key)}
        </span>
      ))}
    </div>
  )
}
